import React, { useCallback, useEffect, useState } from 'react';
import { Search, Filter, RefreshCw, Users, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';

const EMPTY_FILTERS = {
  search: '',
  affiliate: '',
  referral_status: '',
  membership_type: '',
  expiry_from: '',
  expiry_to: '',
  created_from: '',
  created_to: '',
};

const statusBadgeClass = (status) => {
  const value = String(status || '').toLowerCase();
  if (value.includes('gold') || value.includes('titanium') || value.includes('silver') || value.includes('platinum') || value.includes('rhodium')) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (value.includes('docs')) return 'bg-amber-100 text-amber-700';
  if (value.includes('registered')) return 'bg-sky-100 text-sky-700';
  if (value.includes('verification')) return 'bg-indigo-100 text-indigo-700';
  return 'bg-gray-100 text-gray-700';
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB');
};

const AffiliateReferrals = () => {
  const { api } = useAdminAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState({
    affiliates: [],
    referral_statuses: [],
    membership_types: [],
  });

  const fetchReferrals = useCallback(async (nextFilters = EMPTY_FILTERS) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(nextFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/api/admin/affiliate-referrals?${params.toString()}`);
      setReferrals(response.data.referrals || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load affiliate referrals');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/affiliate-referrals/filter-options');
      setFilterOptions({
        affiliates: response.data.affiliates || [],
        referral_statuses: response.data.referral_statuses || [],
        membership_types: response.data.membership_types || [],
      });
    } catch (error) {
      toast.error('Failed to load affiliate referral filters');
    }
  }, [api]);

  useEffect(() => {
    fetchFilterOptions();
    fetchReferrals(EMPTY_FILTERS);
  }, [fetchFilterOptions, fetchReferrals]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchReferrals(filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    fetchReferrals(EMPTY_FILTERS);
  };

  const affiliateOptions = [
    { value: '', label: 'All referrers' },
    ...filterOptions.affiliates.map((affiliate) => ({
      value: affiliate.value,
      label: `${affiliate.label}${affiliate.email ? ` (${affiliate.email})` : ''}`,
    })),
  ];
  const referralStatusOptions = [
    { value: '', label: 'All statuses' },
    ...filterOptions.referral_statuses.map((status) => ({ value: status, label: status })),
  ];
  const membershipTypeOptions = [
    { value: '', label: 'All membership types' },
    ...filterOptions.membership_types.map((tier) => ({ value: tier.value, label: tier.label })),
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Affiliate / Member Referral</h1>
          <p className="text-gray-600 mt-1">Monitor affiliate-referred prospects, conversion status, and membership expiry.</p>
        </div>
        <Button variant="outline" onClick={() => fetchReferrals(filters)}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={18} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={applyFilters} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={filters.search}
                  onChange={(event) => updateFilter('search', event.target.value)}
                  placeholder="Member, email, status"
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Affiliate / Referring Member</Label>
              <SearchableSelect
                options={affiliateOptions}
                value={filters.affiliate}
                onChange={(value) => updateFilter('affiliate', value)}
                placeholder="All referrers"
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label>Referral Status</Label>
              <SearchableSelect
                options={referralStatusOptions}
                value={filters.referral_status}
                onChange={(value) => updateFilter('referral_status', value)}
                placeholder="All statuses"
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label>Membership Type</Label>
              <SearchableSelect
                options={membershipTypeOptions}
                value={filters.membership_type}
                onChange={(value) => updateFilter('membership_type', value)}
                placeholder="All membership types"
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label>Expiry From</Label>
              <Input type="date" value={filters.expiry_from} onChange={(event) => updateFilter('expiry_from', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Expiry To</Label>
              <Input type="date" value={filters.expiry_to} onChange={(event) => updateFilter('expiry_to', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Referral Created From</Label>
              <Input type="date" value={filters.created_from} onChange={(event) => updateFilter('created_from', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Referral Created To</Label>
              <Input type="date" value={filters.created_to} onChange={(event) => updateFilter('created_to', event.target.value)} />
            </div>
            <div className="md:col-span-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={clearFilters}>Clear</Button>
              <Button type="submit">Apply Filters</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users size={18} />
              Affiliate / Member Referrals ({total})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading affiliate referrals...</div>
          ) : referrals.length === 0 ? (
            <div className="py-12 text-center text-gray-500">No affiliate referrals found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Affiliate / Referring Member</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Affiliate Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Referred Member</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Referral Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Membership Expiry Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Referral Created Date</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((referral) => (
                    <tr key={referral.target_id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{referral.affiliate_name}</div>
                        <div className="text-xs text-gray-500">{referral.affiliate_member_name || referral.affiliate_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={statusBadgeClass(referral.affiliate_status)}>{referral.affiliate_status}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{referral.referred_member}</div>
                        <div className="text-xs text-gray-500">{referral.referred_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={statusBadgeClass(referral.membership_type || referral.referral_status)}>
                          {referral.membership_type || referral.referral_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays size={14} className="text-gray-400" />
                          {formatDate(referral.membership_expiry_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatDate(referral.referral_created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateReferrals;
